import { CALL_HISTORY_METHOD } from 'connected-react-router';
import { matchPath } from 'react-router';
import {
  getUserDetails,
  getMeterRemaining,
  setMeterRemaining,
  performAnonymousMeteringSurfCheck,
  getUserPremiumStatus,
} from '@surfline/web-common';
import {
  CamAndForecastsRoute,
  GeonameMapRoute,
  MapPageRoute,
  SpotPageRoute,
  PremiumAnalysisRoute,
  SubregionForecastPageRoute,
  SubregionForecastRoute,
  ChartsPageRoute,
  ChartsPageContainerRoute,
  RateLimitTestPage,
  BuoyRoute,
} from '../routesConfig';
import { PERFORMED_SURF_CHECK_ACTION } from '../actions/metering';
import { performMeteringSurfCheck } from '../common/api/metering';
import { getActiveMeter } from '../selectors/meter';
import { buoysAPI } from '../common/api/buoys';

// Array of action types that can trigger a surf check refresh
const WHITELISTED_SURF_CHECK_ACTIONS = [PERFORMED_SURF_CHECK_ACTION];

const WHITELISTED_LOCATION_CHANGES = [
  CamAndForecastsRoute,
  GeonameMapRoute,
  MapPageRoute,
  SpotPageRoute,
  PremiumAnalysisRoute,
  SubregionForecastPageRoute,
  SubregionForecastRoute,
  ChartsPageRoute,
  ChartsPageContainerRoute,
  RateLimitTestPage,
  BuoyRoute,
];

const performSurfCheck = async (dispatch) => {
  const { meterRemaining } = await performMeteringSurfCheck();
  dispatch(setMeterRemaining(meterRemaining));
};

// TODO: Move this middleware to common and allow extension by passing in whitelisted routes.
export const surfCheckMeteringMiddleware =
  ({ getState, dispatch }) =>
  (next) =>
  async (action) => {
    const state = getState();

    // If the user is not a metered user or has exceeded their surf
    // check limit for the week we can just eagerly return here since
    // we don't need to perform the PATCH call
    const meter = getActiveMeter(state);

    const isMeteredUser = !!meter;
    const isAnonmyous = !getUserDetails(state);
    const isPremium = getUserPremiumStatus(state);
    const hasRemainingSurfChecks = getMeterRemaining(state) !== -1;
    // Page views are specific for Anonymous Sessions
    const hasRemainingPageViews = getMeterRemaining(state) >= 0;
    if (
      isPremium ||
      !isMeteredUser ||
      !hasRemainingSurfChecks ||
      (isAnonmyous && !hasRemainingPageViews)
    ) {
      return next(action);
    }

    if (WHITELISTED_SURF_CHECK_ACTIONS.indexOf(action.type) !== -1 && !isAnonmyous) {
      await performSurfCheck(dispatch);
      return next(action);
    }

    // This action is only dispatch when using `push` from `connected-react-router`.
    // If a regular `Link` component is used, then this action will never fire
    // and the surf check will not be extended
    if (action.type === CALL_HISTORY_METHOD && action.payload.method === 'push') {
      const [pathname] = action.payload?.args;

      // Iterate each whitelisted route to see if the current route
      // change matches a whitelisted route. `.some()` will return
      // as soon as it find a match so we don't perform extra work
      const matchesWhitelistedRoute = WHITELISTED_LOCATION_CHANGES.some((route) =>
        matchPath(pathname, route.routeProps),
      );

      if (matchesWhitelistedRoute) {
        if (isAnonmyous && hasRemainingPageViews) {
          // If it's an anonymous session and has active page view then they
          // have used up there surf check, perform the surf check which
          // will set meter remaining to -1
          await dispatch(performAnonymousMeteringSurfCheck());

          return next(action);
        }

        await performSurfCheck(dispatch);
        return next(action);
      }
    }

    return next(action);
  };

export const apiMiddlewares = [buoysAPI.middleware];
